import { db } from '@config/firebase.config';

const daysRef = (teamId, userId) =>
  db
    .collection('memberData')
    .doc(teamId)
    .collection('daysData')
    .doc(userId)
    .collection('dateId');

const accRef = (teamId, userId) =>
  db.collection('memberData').doc(teamId).collection('accumulator').doc(userId);

/**
 * Normalizes either the accumulator day section or a daysData doc into a
 * consistent shape.  Both sources share the same core fields; callers never
 * need to know which source the data came from.
 */
export function normalizeMemberDay(source) {
  if (!source) return null;
  const totalSec =
    Number(source.totalSec) ||
    Number(source.totalHours || 0) * 3600 +
      Number(source.totalMinutes || 0) * 60;
  const idleSec =
    Number(source.idleSec) ||
    Number(source.idleHours || 0) * 3600 + Number(source.idleMinutes || 0) * 60;
  const productiveSec =
    Number(source.productiveSec) ||
    Number(source.productiveHours || 0) * 3600 +
      Number(source.productiveMinutes || 0) * 60;
  return {
    dateId: source.dateId || '',
    totalSec,
    idleSec,
    productiveSec,
    productivityPercentage:
      source.productivityPercentage != null
        ? Number(source.productivityPercentage)
        : totalSec > 0
          ? Math.round((productiveSec / totalSec) * 10000) / 100
          : 0,
    hourlyTotalSec: source.hourlyTotalSec || {},
    hourlyProductiveSec: source.hourlyProductiveSec || {},
    appUsage: source.appUsage || {},
    screenshotCount: Number(source.screenshotCount) || 0,
    present: source.present ?? false,
    firstLoginAt: source.firstLoginAt ?? null,
    lastPingAt: source.lastPingAt ?? null,
    isLate: source.isLate ?? false,
    alertsCount: Number(source.alertsCount) || 0,
  };
}

export async function fetchMemberAccumulator(teamId, userId) {
  const snap = await accRef(teamId, userId).get();
  return snap.exists ? snap.data() : null;
}

export async function fetchMemberDayData(teamId, userId, dateId) {
  const snap = await daysRef(teamId, userId).doc(dateId).get();
  return snap.exists ? snap.data() : null;
}

/** Fetch the last `limitCount` daysData docs, returned oldest-first. */
export async function fetchMemberRecentDays(teamId, userId, limitCount = 7) {
  const snap = await daysRef(teamId, userId)
    .orderBy('dateId', 'desc')
    .limit(limitCount)
    .get();
  return snap.docs.map((d) => d.data()).reverse();
}

/**
 * Fetch a member's monthly attendance doc from
 * membersDatabase/{userId}/attendanceByMonth/{monthId}.
 * monthId: lowercase "monthname-year" (e.g. "july-2026").
 * Returns { [dateKey]: { present, startTime, endTime } } keyed by "DD:MM:YYYY".
 */
export async function fetchMemberAttendanceMonth(userId, monthId) {
  const snap = await db
    .collection('membersDatabase')
    .doc(userId)
    .collection('attendanceByMonth')
    .doc(monthId)
    .get();
  return snap.exists ? snap.data() : null;
}
